<template>
    <main class="xigcheck-home">
        <section class="hero-section">
            <div class="hero-inner">
                <div class="hero-copy">
                    <div class="eyebrow">XigCheck</div>
                    <h1>
                        Independent Product Review
                        <br />
                        <span class="title-line">&amp; Marketplace <em>Trust Layer</em></span>
                    </h1>
                    <p class="hero-desc">
                        XigCheck is the dedicated product review and marketplace
                        <br />
                        trust support layer affiliated with the XigFor marketplace
                        <br />
                        platform. Built for trust, transparency, and marketplace integrity.
                    </p>
                    <div class="hero-features">
                        <div v-for="feature in heroFeatures" :key="feature.label" class="hero-feature">
                            <img :src="feature.icon" :alt="feature.label" />
                            <span>{{ feature.label }}</span>
                        </div>
                    </div>
                    <div class="hero-actions">
                        <router-link to="/xigcheck/how-it-works" class="primary-btn">Learn How It Works <span>→</span></router-link>
                        <router-link to="/xigcheck/trust-layer" class="outline-btn">Explore Trust Layer</router-link>
                    </div>
                    <div class="hero-note">
                        <svg class="shield-check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            <path d="m9 11 2 2 4-4" />
                        </svg>
                        <span>Supporting selected high-value second-hand product categories.</span>
                    </div>
                </div>

                <div class="hero-visual">
                    <img src="@/assets/xigcheck/home2.png" alt="Luxury goods reviewed by XigCheck" class="hero-product" />
                </div>
            </div>
        </section>

        <section class="trust-strip">
            <div v-for="item in trustItems" :key="item.title" class="trust-item">
                <div class="round-icon"><img :src="item.icon" :alt="item.title" /></div>
                <div>
                    <h3>{{ item.title }}</h3>
                    <p>{{ item.text }}</p>
                </div>
            </div>
        </section>

        <section class="works-section">
            <div class="section-heading">
                <h2>How XigCheck Works</h2>
                <p>A simple and transparent review process to support trust for high-value products.</p>
            </div>
            <div class="steps-row">
                <div v-for="step in steps" :key="step.title" class="step-card">
                    <div class="step-icon"><img :src="step.icon" :alt="step.title" /></div>
                    <strong>{{ step.number }}</strong>
                    <h3>{{ step.title }}</h3>
                    <p>{{ step.text }}</p>
                </div>
            </div>
        </section>

        <section class="categories-section">
            <div class="section-heading">
                <h2>Supported Categories</h2>
                <p>XigCheck provides review support for selected high-value and collectible categories.</p>
            </div>
            <div class="category-grid">
                <article v-for="category in categories" :key="category.title" class="category-card">
                    <div class="mini-search"><img :src="icons.search" alt="" /></div>
                    <img :src="category.image" :alt="category.title.replace('<br />', ' ')" class="category-image" />
                    <h3 v-html="category.title"></h3>
                    <p v-for="line in category.lines" :key="line">{{ line }}</p>
                </article>
            </div>
            <router-link to="/xigcheck/supported-categories" class="text-link">View All Supported Categories -></router-link>
        </section>

        <section class="brands-section">
            <div class="section-heading">
                <h2>Brands Commonly Reviewed</h2>
                <p>Examples of brands that may appear within our review categories.</p>
            </div>
            <div class="brand-row">
                <div v-for="brand in brands" :key="brand.name" class="brand-card">
                    <img :src="brand.image" :alt="brand.name" />
                </div>
            </div>
            <div class="brand-note">
                Brand references are provided solely to identify categories of products that may be reviewed.<br />
                XigCheck is not affiliated with, endorsed by, or officially authorized by the listed brands unless explicitly stated otherwise.
            </div>
            <router-link to="/xigcheck/brand-categories" class="text-link">View Full Brand List &amp; Guidelines -></router-link>
        </section>

        <section class="compliance-section">
            <div class="compliance-inner">
                <div class="compliance-intro">
                    <h2>Compliance &amp; Protection</h2>
                    <p>XigCheck is designed to support marketplace trust and operate in accordance with strict compliance and protection standards.</p>
                    <router-link to="/xigcheck/compliance-protection" class="primary-btn">Learn More</router-link>
                </div>
                <router-link v-for="card in complianceCards" :key="card.title" :to="card.route" class="compliance-card">
                    <img :src="card.icon" :alt="card.title" />
                    <h3>{{ card.title }}</h3>
                    <p>{{ card.text }}</p>
                    <span>Learn More -></span>
                </router-link>
            </div>
        </section>

        <section class="related-section">
            <strong>Related Policies</strong>
            <router-link v-for="policy in relatedPolicies" :key="policy.label" :to="policy.route">
                <img :src="policy.icon" alt="" />
                {{ policy.label }} <span>></span>
            </router-link>
        </section>
    </main>
</template>

<script>
import check2Icon from '@/assets/xigcheck/check2.png'
import compassIcon from '@/assets/xigcheck/upde.png'
import dollarIcon from '@/assets/xigcheck/currency-coin-dollar.png'
import cardIcon from '@/assets/xigcheck/card-02.png'
import lockIcon from '@/assets/xigcheck/lock.png'
import lockOpenIcon from '@/assets/xigcheck/lock-open-02.png'
import searchIcon from '@/assets/xigcheck/search-02.png'
import userGroupIcon from '@/assets/xigcheck/user-profile-group.png'
import vectorIcon from '@/assets/xigcheck/Vector.png'
import iconImage from '@/assets/xigcheck/zfx.png'
import icon2Image from '@/assets/xigcheck/Icon2.png'
import shieldCheckIcon from '@/assets/xigcheck/sasety.png'
import luxuryAccessories from '@/assets/xigcheck/Luxury Accessories.png'
import luxuryEyewear from '@/assets/xigcheck/Luxury Eyewear.png'
import luxuryFashion from '@/assets/xigcheck/Luxury Fashion & Apparel.png'
import premiumSneakers from '@/assets/xigcheck/Premium Sneakers.png'
import homeGoods from '@/assets/xigcheck/Home & Lifestyle Goods.png'
import homeGoodsOne from '@/assets/xigcheck/Home & Lifestyle Goods（1）.png'
import homeGoodsTwo from '@/assets/xigcheck/Home & Lifestyle Goods（2）.png'
import louisVuitton from '@/assets/xigcheck/Louis Vuitton.png'
import bag from '@/assets/xigcheck/image_11.png'
import chanel from '@/assets/xigcheck/Chanel.png'
import hermes from '@/assets/xigcheck/Hermes.png'
import gucci from '@/assets/xigcheck/Gucci.png'
import rolex from '@/assets/xigcheck/Rolex.png'
import nike from '@/assets/xigcheck/Nike.png'
import adidas from '@/assets/xigcheck/Adidas.png'
import dior from '@/assets/xigcheck/Dior.png'
import prada from '@/assets/xigcheck/Prada.png'
import cartier from '@/assets/xigcheck/Cartier.png'

import trust1 from '@/assets/xigcheck/Background-2.png'
import trust2 from '@/assets/xigcheck/Background-1.png'
import trust3 from '@/assets/xigcheck/Background.png'
import svgMargin from '@/assets/xigcheck/SVG_margin.png'
import linkIcon from '@/assets/xigcheck/link.png'
import compass from '@/assets/xigcheck/compass.png'
export default {
    name: 'XigCheckHomePC',
    data() {
        return {
            icons: {
                check2: check2Icon,
                search: searchIcon
            },
            heroFeatures: [
                { label: 'AI-Assisted Review', icon: compass },
                { label: 'Expert Assessment', icon: searchIcon },
                { label: 'Third-Party Support', icon: userGroupIcon }
            ],
            trustItems: [
                {
                    title: 'Marketplace Trust',
                    text: 'Supporting transparency and marketplace integrity for high-value second-hand products.',
                    icon: trust1
                },
                {
                    title: 'Independent Review',
                    text: 'Combining AI technology, expert assessment, and third-party authentication support.',
                    icon: trust2
                },
                {
                    title: 'Fraud Prevention',
                    text: 'Helping reduce counterfeit risks and suspicious activities through multi-layer review procedures.',
                    icon: trust3
                }
            ],
            steps: [
                {
                    number: '01',
                    title: 'Submit Product',
                    text: 'Users submit product images and listing details.',
                    icon: vectorIcon
                },
                {
                    number: '02',
                    title: 'Review Process',
                    text: 'AI-assisted analysis, expert assessment, and third-party review support.',
                    icon: searchIcon
                },
                {
                    number: '03',
                    title: 'Review Outcome',
                    text: 'Review outcomes represent independent assessment opinions only.',
                    icon: shieldCheckIcon
                }
            ],
            categories: [
                {
                    title: 'Luxury<br />Handbags',
                    image: bag,
                    lines: ['designer handbags', 'Leather handbags', 'Limited edition', 'Collectible handbags']
                },
                {
                    title: 'Luxury<br />Accessories',
                    image: luxuryAccessories,
                    lines: ['Wallets', 'Belts', 'Scarves', 'fashion accessories', 'Small leather goods']
                },
                {
                    title: 'Premium<br />Sneakers',
                    image: premiumSneakers,
                    lines: ['Premium sneakers', 'Collectible sneakers', 'Designer footwear', 'Limited-release footwear']
                },
                {
                    title: 'Luxury Fashion<br />& Apparel',
                    image: luxuryFashion,
                    lines: ['Luxury clothing', 'Collectible streetwear', 'Designer outerwear']
                },
                {
                    title: 'Luxury<br />Eyewear',
                    image: luxuryEyewear,
                    lines: ['Sunglasses', 'Designer eyewear', 'Collectible eyewear']
                },
                {
                    title: 'Luxury<br />Watches',
                    image: homeGoodsTwo,
                    lines: ['Luxury Timepieces', 'Sports & Chronograph Watches', 'Vintage & Pocket Watches']
                },
                {
                    title: 'Fine Jewelry<br />& Accessories',
                    image: homeGoodsOne,
                    lines: ['Luxury rings & bands', 'Fine necklaces', 'Gemstone jewelry', 'Collectible pieces']
                },
                {
                    title: 'Home<br />& Lifestyle Goods',
                    image: homeGoods,
                    lines: ['selected home accessories', 'selected decorative goods']
                }
            ],
            brands: [
                { name: 'Louis Vuitton', image: louisVuitton },
                { name: 'Chanel', image: chanel },
                { name: 'Hermes', image: hermes },
                { name: 'Gucci', image: gucci },
                { name: 'Rolex', image: rolex },
                { name: 'Nike', image: nike },
                { name: 'Adidas', image: adidas },
                { name: 'Dior', image: dior },
                { name: 'Prada', image: prada },
                { name: 'Cartier', image: cartier }
            ],
            complianceCards: [
                {
                    title: 'Fraud Prevention',
                    text: 'Proactive risk control and counterfeit prevention systems.',
                    route: '/xigcheck/fraud-prevention',
                    icon: lockIcon
                },
                {
                    title: 'Independent Review',
                    text: 'Structured review procedures by experts and partners.',
                    route: '/xigcheck/independent-review',
                    icon: shieldCheckIcon
                },
                {
                    title: 'No Escrow',
                    text: 'We do not hold or custody user funds.',
                    route: '/xigcheck/no-escrow',
                    icon: cardIcon
                },
                {
                    title: 'No Financial Services',
                    text: 'XigCheck does not provide financial, insurance, or investment services.',
                    route: '/xigcheck/no-financial-services',
                    icon: dollarIcon
                }
            ],
            relatedPolicies: [
                { label: 'Terms of Service', route: '/xigcheck/terms-of-service', icon: icon2Image },
                { label: 'Privacy Policy', route: '/xigcheck/privacy-policy', icon: lockOpenIcon },
                { label: 'Refund Policy', route: '/xigcheck/refund-policy', icon: compassIcon },
                { label: 'Authentication Process Policy', route: '/xigcheck/authentication-process-policy', icon: svgMargin },
                { label: 'Fraud Prevention Policy', route: '/xigcheck/fraud-prevention-policy', icon: linkIcon },
                { label: 'High-value Goods Policy', route: '/xigcheck/high-value-goods-policy', icon: iconImage }
            ]
        }
    }
}
</script>

<style scoped>
.xigcheck-home {
    width: 100%;
    min-width: 1100px;
    overflow-x: hidden;
    color: #252633;
    background: #fff;
    font-family: 'Poppins', Arial, sans-serif;
}
.hero-section {
    background: radial-gradient(circle at 0% 0%, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 75%), linear-gradient(90deg, #fff3f7 0%, #ffe6ef 100%);
}
.hero-inner {
    width: 100%;
    min-width: 1100px;
    max-width: 1320px;
    min-height: 640px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 80px 48px;
    box-sizing: border-box;
}
.hero-copy {
    flex: 0 0 620px;
    width: 620px;
    align-self: center;
}
.eyebrow {
    display: inline-flex;
    align-items: center;
    height: 35px;
    padding: 0 17px;
    border-radius: 99px;
    background: #fdeaf1;
    color: #f43f5e;
    font-size: 18px;
    font-weight: 400;
    letter-spacing: 0.05em;
}
.hero-copy h1 {
    margin: 24px 0 20px;
    font-size: 40px;
    line-height: 1.15;
    font-weight: 700;
    color: #171927;
    letter-spacing: -0.015em;
}
.hero-copy h1 .title-line {
    white-space: nowrap;
}
.hero-copy h1 em {
    color: #ff4569;
    font-style: normal;
}
.hero-desc {
    width: 500px;
    margin: 0 0 28px;
    color: #5f6472;
    font-size: 14px;
    line-height: 1.7;
    font-weight: 500;
}
.hero-features {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 32px;
}
.hero-feature {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #252633;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
}
.hero-feature:not(:last-child)::after {
    content: '';
    display: inline-block;
    width: 1px;
    height: 14px;
    background-color: #e5e7eb;
    margin-left: 20px;
}
.hero-feature img {
    width: 18px;
    height: 18px;
    object-fit: contain;
}
.hero-actions {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 28px;
}
.primary-btn,
.outline-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    border-radius: 99px;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    transition: all 0.2s ease;
}
.primary-btn {
    min-width: 218px;
    padding: 0 24px;
    background: #ff4569;
    color: #fff;
    box-shadow: 0 10px 24px rgba(255, 69, 105, 0.2);
}
.primary-btn span {
    margin-left: 8px;
}
.primary-btn:hover {
    background: #e03d5c;
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(255, 69, 105, 0.3);
}
.outline-btn {
    min-width: 206px;
    border: 1.5px solid #ff4569;
    color: #ff4569;
    background: #fff;
}
.outline-btn:hover {
    background: #fff0f3;
    transform: translateY(-1px);
}
.hero-note {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #858895;
    font-size: 13px;
    font-weight: 400;
}
.shield-check-icon {
    width: 16px;
    height: 16px;
    stroke: #858895;
    flex-shrink: 0;
}
.hero-visual {
    flex: 0 0 580px;
    width: 580px;
    height: 444px;
    align-self: center;
}
.hero-product {
    width: 580px;
    height: 444px;
    display: block;
    object-fit: contain;
    filter: drop-shadow(0 22px 34px rgba(88, 57, 64, 0.08));
}
.trust-strip {
    width: 1280px;
    height: 244px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: center;
}
.trust-item {
    display: flex;
    gap: 16px;
    min-height: 130px;
    padding: 0 58px;
    border-right: 1px solid #eef0f5;
}
.trust-item:last-child {
    border-right: none;
}
.round-icon,
.step-icon {
    flex: 0 0 auto;
    width: 66px;
    height: 66px;
    border-radius: 50%;
    background: #fff0f5;
    display: flex;
    align-items: center;
    justify-content: center;
}
.round-icon img {
    width: 66px;
    height: 66px;
    object-fit: cover;
}
.trust-item h3,
.step-card h3,
.category-card h3,
.compliance-card h3 {
    margin: 0;
    color: #292b38;
    font-weight: 800;
}
.trust-item h3 {
    font-size: 20px;
    line-height: 1.2;
}
.trust-item p {
    width: 260px;
    margin: 13px 0 0;
    color: #5f6472;
    font-size: 14px;
    line-height: 24px;
    font-weight: 400;
}
.works-section {
    background: #f7f8fa;
    padding: 56px 0 70px;
    box-sizing: border-box;
}
.section-heading {
    text-align: center;
}
.section-heading h2 {
    margin: 0;
    color: #202230;
    font-size: 30px;
    line-height: 1.2;
    font-weight: 800;
}
.section-heading p {
    margin: 18px 0 0;
    color: #5f6472;
    font-size: 14px;
    font-weight: 500;
}
.steps-row {
    position: relative;
    width: 1200px;
    margin: 46px auto 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 270px;
}
.step-card {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
}
.step-card::after {
    content: '';
    position: absolute;
    left: 42px;
    top: 42px;
    width: calc(100% + 270px);
    border-top: 2px dotted #ff9caf;
    z-index: -1;
}
.step-card:last-child::after {
    display: none;
}
.step-icon {
    width: 84px;
    height: 84px;
    margin-bottom: 26px;
    border-radius: 50%;
    background: #fff0f5;
    display: flex;
    align-items: center;
    justify-content: center;
}
.step-icon img {
    width: 34px;
    height: 34px;
    object-fit: contain;
}
.step-card strong {
    display: block;
    color: #ff4569;
    font-size: 15px;
    margin-bottom: 8px;
    font-weight: 700;
}
.step-card h3 {
    font-size: 18px;
    margin-bottom: 10px;
}
.step-card p {
    margin: 0;
    color: #777b88;
    font-size: 13px;
    line-height: 20px;
    font-weight: 400;
}
.categories-section {
    padding: 46px 0 48px;
}
.category-grid {
    width: 1420px;
    margin: 48px auto 28px;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 20px;
}
.category-card {
    position: relative;
    min-height: 302px;
    padding: 25px 0 20px 0;
    border: 1px solid #eef1f6;
    border-radius: 16px;
    background: #fff;
    text-align: center;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
    box-sizing: border-box;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.category-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.08);
}
.mini-search {
    position: absolute;
    left: 16px;
    top: 16px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #fff0f5;
    display: flex;
    align-items: center;
    justify-content: center;
}
.mini-search img {
    width: 14px;
    height: 14px;
    object-fit: contain;
}
.category-image {
    width: 128px;
    height: 128px;
    object-fit: contain;
    margin-bottom: 22px;
}
.category-card h3 {
    min-height: 40px;
    font-size: 15px;
    line-height: 20px;
    margin-bottom: 12px;
}
.category-card p {
    margin: 0;
    color: #8a8e99;
    font-size: 10px;
    line-height: 16px;
    font-weight: 500;
}
.text-link {
    display: flex;
    justify-content: center;
    color: #ff4569;
    font-size: 15px;
    font-weight: 800;
    text-decoration: none;
}
.brands-section {
    padding: 0 0 58px;
}
.brand-row {
    width: 980px;
    margin: 28px auto 20px;
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    gap: 16px;
}
.brand-card {
    /* height: 72px; */
    /* border: 1px solid #e8ebf1; */
    display: flex;
    align-items: center;
    justify-content: center;
}
.brand-card img {
    width: 94px;
    height: 94px;
    object-fit: contain;
}
.brand-note {
    width: 670px;
    margin: 0 auto 28px;
    padding: 14px 20px;
    border-radius: 8px;
    background: #fafafa;
    color: #6d7280;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
}
.compliance-section {
    background: #000;
    padding: 72px 0 70px;
    box-sizing: border-box;
}
.compliance-inner {
    max-width: 1420px;
    margin: 0 auto;
    padding: 0 32px;
    display: grid;
    grid-template-columns: 260px repeat(4, 1fr);
    gap: 20px;
    box-sizing: border-box;
}
.compliance-intro {
    padding: 0;
}
.compliance-intro h2 {
    margin: 0 0 18px;
    color: #fff;
    font-size: 30px;
    line-height: 1.2;
    font-weight: 800;
}
.compliance-intro p {
    width: 224px;
    margin: 0 0 44px;
    color: #a4acba;
    font-size: 13px;
    line-height: 22px;
    font-weight: 400;
}
.compliance-intro .primary-btn {
    min-width: 116px;
    height: 44px;
    font-size: 12px;
    border-radius: 9px;
}
.compliance-card {
    min-height: 250px;
    height: auto;
    padding: 30px 24px;
    border-radius: 8px;
    background: #121922;
    text-decoration: none;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.compliance-card:hover {
    transform: translateY(-4px);
    background: #19222d;
}
.compliance-card img {
    width: 28px;
    height: 28px;
    object-fit: contain;
    margin-bottom: 24px;
}
.compliance-card h3 {
    color: #fff;
    font-size: 16px;
    margin: 0 0 12px 0;
}
.compliance-card p {
    margin: 0 0 16px 0;
    color: #a8afbb;
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
}
.compliance-card span {
    display: inline-flex;
    margin-top: auto;
    color: #ff4569;
    font-size: 12px;
    font-weight: 700;
}
.related-section {
    height: 74px;
    /* padding: 0 64px; */
    border-bottom: 1px solid #ecedf2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 50px;
    box-sizing: border-box;
}
.related-section strong {
    color: #262937;
    font-size: 12px;
    font-weight: 800;
    margin-right: 24px;
}
.related-section a {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: #697080;
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;
    white-space: nowrap;
}
.related-section img {
    width: 14px;
    height: 14px;
    object-fit: contain;
}
.related-section span {
    color: #9ca1ae;
    font-size: 15px;
}
</style>
